import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api/client";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { SLABadge } from "@/components/SLABadge";
import { STATUSES, STATUS_LABELS, fmtDateTime } from "@/lib/constants";
import { ArrowLeft, MessageSquare, Cpu, User as UserIcon, Loader2, Paperclip, Upload, FileText, Trash2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CALL_TYPE_LABELS } from "@/pages/Tickets";

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [spares, setSpares] = useState([]);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [spareForm, setSpareForm] = useState({ spare_id: "", part_name: "", part_number: "", brand: "", quantity: 1, unit_price: 0, remarks: "" });
  const [addingSpare, setAddingSpare] = useState(false);

  const load = async () => {
    try {
      const [t, e, s] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get("/users/engineers"),
        api.get("/spares"),
      ]);
      setTicket(t.data); setEngineers(e.data); setSpares(s.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const setStatus = async (s) => {
    try { await api.post(`/tickets/${id}/status`, { status: s }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const setEngineer = async (eid) => {
    try { await api.patch(`/tickets/${id}`, { assigned_engineer_id: eid || null }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/tickets/${id}/notes`, { note }); setNote(""); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const pickSpare = (sid) => {
    if (!sid) return setSpareForm((f) => ({ ...f, spare_id: "", part_name: "", part_number: "", brand: "" }));
    const s = spares.find((x) => x.id === sid);
    if (!s) return;
    setSpareForm((f) => ({ ...f, spare_id: sid, part_name: s.part_name, part_number: s.part_number || "", brand: s.brand || "" }));
  };
  const addSpareItem = async () => {
    if (!spareForm.part_name.trim() && !spareForm.spare_id) return;
    setAddingSpare(true);
    try {
      const payload = {
        spare_id: spareForm.spare_id || null,
        part_name: spareForm.part_name.trim() || null,
        part_number: spareForm.part_number.trim() || null,
        brand: spareForm.brand.trim() || null,
        quantity: Math.max(1, Number(spareForm.quantity) || 1),
        unit_price: Number(spareForm.unit_price) || 0,
        remarks: spareForm.remarks.trim() || null,
      };
      await api.post(`/tickets/${id}/spare-items`, payload);
      setSpareForm({ spare_id: "", part_name: "", part_number: "", brand: "", quantity: 1, unit_price: 0, remarks: "" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setAddingSpare(false); }
  };
  const removeSpareItem = async (idx) => {
    if (!window.confirm("Remove this spare item?")) return;
    try { await api.delete(`/tickets/${id}/spare-items/${idx}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  if (!ticket) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> <span className="ml-2 text-sm">Loading ticket…</span>
      </div>
    );
  }

  const canEdit = ["super_admin", "company_admin", "dispatcher"].includes(user?.role);
  const canStatus = canEdit || user?.role === "engineer";

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
      <button data-testid="ticket-back" onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-sm text-muted-foreground">{ticket.ticket_no}</p>
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
            {ticket.call_type && (
              <span data-testid="ticket-call-type-badge" className="inline-flex rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{CALL_TYPE_LABELS[ticket.call_type] || ticket.call_type}</span>
            )}
            <SLABadge status={ticket.sla_status} remainingMin={ticket.sla_remaining_min} />
          </div>
          <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">{ticket.complaint}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Created {fmtDateTime(ticket.created_at)}</p>
        </div>
        {canStatus && (
          <div className="flex items-center gap-2 flex-wrap">
            <a
              data-testid="ticket-download-pdf"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${ticket.id}/job-sheet`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary"
            >
              <FileText className="h-3.5 w-3.5" /> Download job sheet PDF
            </a>
            <label className="label-eyebrow">Status</label>
            <select
              data-testid="ticket-detail-status"
              value={ticket.status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
            {canEdit && (
              <button
                data-testid="ticket-detail-delete"
                onClick={async () => {
                  if (!window.confirm(`Delete ${ticket.ticket_no} permanently? This cannot be undone.`)) return;
                  try {
                    await api.delete(`/tickets/${ticket.id}`);
                    navigate("/app/tickets", { replace: true });
                  } catch (e) { setErr(formatApiError(e)); }
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/20"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
          </div>
        )}
      </header>

      {err && <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="sharp-card p-5 lg:col-span-2">
          <p className="label-eyebrow">Description</p>
          <p className="mt-2 whitespace-pre-wrap text-sm">{ticket.description || "—"}</p>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <Info label="Customer" value={ticket.customer?.company_name} />
            <Info label="Contact" value={`${ticket.contact_person || "—"} · ${ticket.contact_phone || "—"}`} />
            <Info label="Equipment" value={ticket.equipment ? `${ticket.equipment.name} (${ticket.equipment.brand || ""} ${ticket.equipment.model || ""})` : "—"} />
            <Info label="Branch / Location" value={ticket.branch_location || "—"} />
          </div>
        </section>

        <section className="sharp-card p-5">
          <p className="label-eyebrow">Assignment</p>
          <div className="mt-3 flex items-center gap-3 rounded-md border border-border/60 p-3">
            <UserIcon className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{ticket.engineer?.name || "Unassigned"}</p>
              <p className="truncate text-xs text-muted-foreground">{ticket.engineer?.email || "—"}</p>
            </div>
          </div>
          {canEdit && (
            <select
              data-testid="ticket-detail-engineer"
              value={ticket.assigned_engineer_id || ""}
              onChange={(e) => setEngineer(e.target.value)}
              className="mt-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Unassigned</option>
              {engineers.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.email}</option>)}
            </select>
          )}
        </section>

        <section className="sharp-card p-5 lg:col-span-2">
          <p className="label-eyebrow flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5" /> Notes & activity</p>
          <div className="mt-3 space-y-2">
            {(ticket.notes || []).map((n, i) => (
              <div key={i} className="rounded-md border border-border/60 bg-secondary/30 px-3 py-2 text-sm">
                <p className="mb-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="font-semibold text-foreground">{n.by_name}</span>
                  <span>·</span>
                  <span>{fmtDateTime(n.at)}</span>
                </p>
                <p className="whitespace-pre-wrap">{n.note}</p>
              </div>
            ))}
            {(!ticket.notes || ticket.notes.length === 0) && <p className="text-xs text-muted-foreground">No notes yet.</p>}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="ticket-note-input"
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note (visible to team)…"
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <button data-testid="ticket-note-submit" onClick={addNote} className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">Add</button>
          </div>
        </section>

        <section className="sharp-card p-5">
          <p className="label-eyebrow flex items-center gap-2"><Cpu className="h-3.5 w-3.5" /> Spare Item</p>
          <div className="mt-3 space-y-2">
            {(ticket.spare_items || []).map((p, i) => (
              <div key={i} data-testid={`ticket-spare-row-${i}`} className="group flex items-start justify-between gap-2 rounded-md border border-border/60 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{p.part_name}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span>Qty <b className="text-foreground">{p.quantity}</b></span>
                    {Number(p.unit_price) > 0 && <span>₹{Number(p.unit_price).toFixed(2)}</span>}
                    {p.part_number && <span className="font-mono">{p.part_number}</span>}
                    {p.brand && <span>{p.brand}</span>}
                    {p.source === "master" && p.part_id && <span className="rounded-sm bg-primary/10 px-1 py-0.5 font-mono text-primary">{p.part_id}</span>}
                    {p.source === "manual" && <span className="rounded-sm bg-amber-500/10 px-1 py-0.5 text-amber-600">manual</span>}
                  </div>
                  {p.remarks && <p className="mt-0.5 text-[11px] italic text-muted-foreground">{p.remarks}</p>}
                </div>
                <button
                  data-testid={`ticket-spare-remove-${i}`}
                  onClick={() => removeSpareItem(i)}
                  className="rounded-md border border-border/70 p-1 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {(!ticket.spare_items || ticket.spare_items.length === 0) && <p className="text-xs text-muted-foreground">None yet.</p>}
          </div>

          <div className="mt-4 space-y-2 rounded-md border border-dashed border-border/60 p-3">
            <p className="label-eyebrow">Add spare</p>
            <select
              data-testid="ticket-spare-picker"
              value={spareForm.spare_id}
              onChange={(e) => pickSpare(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Manual entry —</option>
              {spares.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.part_id} · {s.part_name} {s.brand ? `(${s.brand})` : ""}{typeof s.stock_quantity === "number" ? ` · stock ${s.stock_quantity}` : ""}
                </option>
              ))}
            </select>
            <input
              data-testid="ticket-spare-name"
              value={spareForm.part_name}
              onChange={(e) => setSpareForm({ ...spareForm, part_name: e.target.value })}
              placeholder="Part name *"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="ticket-spare-partno"
                value={spareForm.part_number}
                onChange={(e) => setSpareForm({ ...spareForm, part_number: e.target.value })}
                placeholder="Part No"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <input
                data-testid="ticket-spare-brand"
                value={spareForm.brand}
                onChange={(e) => setSpareForm({ ...spareForm, brand: e.target.value })}
                placeholder="Brand"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="ticket-spare-qty"
                type="number" min="1" value={spareForm.quantity}
                onChange={(e) => setSpareForm({ ...spareForm, quantity: e.target.value })}
                placeholder="Quantity"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <input
                data-testid="ticket-spare-price"
                type="number" min="0" step="0.01" value={spareForm.unit_price}
                onChange={(e) => setSpareForm({ ...spareForm, unit_price: e.target.value })}
                placeholder="Unit ₹"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <input
              data-testid="ticket-spare-remarks"
              value={spareForm.remarks}
              onChange={(e) => setSpareForm({ ...spareForm, remarks: e.target.value })}
              placeholder="Remarks (optional)"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <button
              data-testid="ticket-spare-add"
              onClick={addSpareItem}
              disabled={addingSpare || (!spareForm.part_name.trim() && !spareForm.spare_id)}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              <Plus className="h-3.5 w-3.5" /> {addingSpare ? "Adding…" : "Add spare"}
            </button>
          </div>
        </section>

        <section className="sharp-card p-5 lg:col-span-3">
          <p className="label-eyebrow flex items-center gap-2"><Paperclip className="h-3.5 w-3.5" /> Attachments</p>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {(ticket.attachments || []).map((a) => (
              <AttachmentThumb key={a.file_id} att={a} />
            ))}
            {(!ticket.attachments || ticket.attachments.length === 0) && (
              <p className="col-span-full text-xs text-muted-foreground">No files uploaded.</p>
            )}
          </div>
          {canStatus && (
            <div className="mt-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs font-medium hover:bg-secondary">
                <Upload className="h-3.5 w-3.5" />
                Upload file
                <input
                  data-testid="ticket-file-input"
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData();
                    fd.append("file", f); fd.append("kind", "attachment"); fd.append("ticket_id", id);
                    try { await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); }
                    catch (err) { setErr(formatApiError(err)); }
                    finally { e.target.value = ""; }
                  }}
                />
              </label>
            </div>
          )}
        </section>

        <section className="sharp-card p-5 lg:col-span-3">
          <p className="label-eyebrow">Status history</p>
          <ol className="mt-3 space-y-2 border-l border-border/60 pl-4">
            {(ticket.status_history || []).map((h, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[22px] top-1 h-2 w-2 rounded-full bg-primary" />
                <div className="flex items-center gap-2">
                  <StatusBadge status={h.status} />
                  <span className="text-xs text-muted-foreground">{fmtDateTime(h.at)}</span>
                </div>
                {h.note && <p className="mt-1 text-xs text-muted-foreground">{h.note}</p>}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="label-eyebrow">{label}</p>
      <p className="mt-1 text-sm">{value || "—"}</p>
    </div>
  );
}

function AttachmentThumb({ att }) {
  const isImage = (att.content_type || "").startsWith("image/");
  const token = null; // rely on cookie; but img can also use ?auth
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${att.file_id}`;
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/files/${att.file_id}`, { responseType: "blob" });
        if (alive) setBlob(URL.createObjectURL(r.data));
      } catch { /* ignore */ }
    })();
    return () => { alive = false; if (blob) URL.revokeObjectURL(blob); };
     
  }, [att.file_id]);
  return (
    <a
      href={blob || url} target="_blank" rel="noreferrer"
      data-testid={`attachment-${att.file_id}`}
      className="group block overflow-hidden rounded-md border border-border/60"
    >
      {isImage && blob ? (
        <img src={blob} alt={att.filename || "attachment"} className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
      ) : (
        <div className="flex aspect-square items-center justify-center bg-secondary/40">
          <Paperclip className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
      <div className="border-t border-border/60 px-2 py-1 text-[10px] text-muted-foreground">
        <p className="truncate">{att.filename || att.kind}</p>
        <p className="uppercase tracking-wider">{att.kind}</p>
      </div>
    </a>
  );
}
